import { Button, ButtonMode, Icon, ICONS, Loader, Popup } from '@creditsights/styleguide-web';
import { range } from 'lodash';
import React, { ReactElement, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CompareText from '../../../components/compare-text/CompareText';
import { COVENANT_COMPARE_FIELDS } from '../../../Constants';
import useFetchCovenantCompareDetails from '../../../hooks/covenant-compare/useFetchCovenantCompareDetails';
import './CovenantCompareDetails.scss';
import '../CompareDetails.scss';

function CovenantCompareDetails(): ReactElement {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [ids, setIds] = useState<string[]>([]);
  const [visibleFields, setVisibleFields] = useState<number[]>(range(30));
  const { data, isLoading } = useFetchCovenantCompareDetails(ids);

  useEffect(() => {
    if (search) {
      setIds(JSON.parse(decodeURI(search.substring(1))));
    } else {
      setIds([]);
    }
  }, [search]);

  const renderCell = (cls, value, key?) => (
    <td key={key}>
      <div className="cell">
        <div className={cls}>
          <pre>{value}</pre>
        </div>
      </div>
    </td>
  );

  const removeLoan = (id) => {
    setIds((values) => {
      const index = values.indexOf(id);
      values.splice(index, 1);
      return [...values];
    });
  };

  const updateBenchmark = (id) => {
    setIds((values) => {
      const index = values.indexOf(id);
      values.splice(index, 1);
      return [id, ...values];
    });
  };

  const renderCells = (fieldIndex, expanded) => {
    return ids.map((id) => {
      const main = data[ids[0]].detailed_fields[`field${fieldIndex + 1}`];
      const fields = data[id].detailed_fields;
      const field = fields[`field${fieldIndex + 1}`];
      return renderCell('value', <CompareText text1={main} text2={field} expanded={expanded} />);
    });
  };

  const toggleVisibleField = (index) => {
    const fieldIndex = visibleFields.indexOf(index);

    if (fieldIndex === -1) {
      visibleFields.push(index);
    } else {
      visibleFields.splice(fieldIndex, 1);
    }

    setVisibleFields([...visibleFields]);
  };

  return (
    <div className="cs-covenant-compare-details-ctx compare-details">
      {isLoading && (
        <div className="loading-ctx">
          <Loader className="cs-loader" position="center" spinnerSize="25px" />
        </div>
      )}

      {!isLoading && data && (
        <>
          <div className="header-ctx">
            <div className="back-link">
              <span onClick={() => navigate(-1)}>
                <i className="angle left icon" /> Back to covenants
              </span>
            </div>
            <div className="download">
              <Button mode={ButtonMode.TERTIARY}>
                <Icon name={ICONS.Download} width={18} height={18} />
              </Button>
            </div>
          </div>
          <div className="table-ctx">
            <table className="compare-table">
              <tr>
                {renderCell('header category company', 'Covenants')}
                {ids.map((id, index) => (
                  <td key={id}>
                    <div className="cell">
                      <div className="value category company">
                        <div className="text">
                          <div>{data[id].issuer}</div>
                          <div>{data[id].issue_name}</div>
                        </div>
                        <div className="actions">
                          {index !== 0 && (
                            <Popup
                              trigger={
                                <div className="benchmark" onClick={() => updateBenchmark(id)}>
                                  Benchmark
                                </div>
                              }
                              content="Make this benchmark"
                              position="top center"
                              basic={false}
                              offset={[0, 8]}
                            />
                          )}
                          <i className="times icon" onClick={() => removeLoan(id)} />
                        </div>
                      </div>
                    </div>
                  </td>
                ))}
              </tr>
              {COVENANT_COMPARE_FIELDS.map((field, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={index}>
                  {renderCell(
                    'header',
                    <div className="expander" onClick={() => toggleVisibleField(index)}>
                      <div className="expander-text">{field}</div>
                      <i className={`angle ${visibleFields.indexOf(index) === -1 ? 'down' : 'up'} icon`} />
                    </div>
                  )}
                  {renderCells(index, visibleFields.indexOf(index) !== -1)}
                </tr>
              ))}
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default CovenantCompareDetails;
